import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';

import PageContainer from 'components/common/page/page-container';
import PageHeader from 'components/common/page/page-header';
import PageBody from 'components/common/page/page-body';
import { SearchInput } from 'components/common/search-input';
import { useFetchMore, useModal } from '../../global-hooks';
import { UI_MODALS } from '../../../helpers/enums';
import { useQuery } from '@apollo/react-hooks';
import LoadMoreButton from '../../common/buttons/load-more-btn/load-more-btn';
import { BusinessGroup, PageInfo } from './types';
import { BUSINESS_GROUPS_GQL } from '../../../graphql/queries/business-groups-query';
import BusinessGroupItem from './BusinessGroupItem';
import ColorButton from '../../common/buttons/color-button';

const BusinessGroups: React.FC = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const debouncedSetSearch = debounce(text => setSearch(text), 500);
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => debouncedSetSearch(e.target.value);
  const { initModal } = useModal();

  const {
    isLoading: isFetchMoreLoading,
    fetchMore: fetchMoreBusinessGroups,
  } = useFetchMore('business_groups');

  const { loading, error, data, refetch, fetchMore } = useQuery<{
    business_groups: { nodes: [BusinessGroup]; pageInfo: PageInfo; totalCount: number };
  }>(BUSINESS_GROUPS_GQL, {
    fetchPolicy: 'network-only',
    variables: { first: 10, byName: search },
  });

  const handleGetMore = () => {
    fetchMoreBusinessGroups(fetchMore, {
      cursor: data?.business_groups.pageInfo.endCursor,
    });
  };

  const handleAddNew = () => {
    initModal(UI_MODALS.BUSINESS_GROUP, {
      onSuccess: () => {
        refetch();
      }
    });
  };

  useEffect(() => {
    refetch();
  }, [search, refetch]);

  const groups = data?.business_groups?.nodes || [];

  return (
    <PageContainer>
      <PageHeader title={t('businessGroupsPage.title')}>
        <div className="flex items-center space-x-4">
          <SearchInput
            placeholder={t('businessGroupsPage.search_by_name')}
            value={search}
            onChange={handleSearchChange}
          />
          <ColorButton color={'green'} type={'inverted'} className='w-40' onClick={handleAddNew}>
            {t('common.add_new')}
          </ColorButton>
        </div>
      </PageHeader>
      <PageBody isLoading={loading} error={error && t('errors.generic')}>
        <div className="my-10">
          {groups.map(group => (
            <BusinessGroupItem key={group.id} group={group} onUpdateSuccess={() => {refetch();}} />
          ))}
        </div>
        {data?.business_groups.pageInfo.hasNextPage && (
          <div className='mt-10 mb-2 text-center'>
            <LoadMoreButton isLoading={isFetchMoreLoading} onClick={handleGetMore} />
          </div>
        )}
      </PageBody>
    </PageContainer>
  );
};

export default BusinessGroups;
